import { useCallback, useEffect, useRef, useState } from "react";
import { useAppConfig } from "@/appConfig";
import { getUserType } from "@/helpers/common";
import {
  isMessageComplianceActive,
  COMPLIANCE_DEBOUNCE_MS,
} from "@/helpers/messageCompliance";
import { checkMessageCompliance } from "@/services/v2/messageComplianceService";

const INERT = { violations: [], severity: "none" };

// Drives the message-compliance banner for an agent's draft message.
// - Runs the `screen` (regex + LLM, unpersisted, cached) check on a debounce while typing.
// - Exposes `runFullCheck()` for the authoritative `full` (regex + LLM) check,
//   meant to be awaited once on Send.
// Inert (no calls, no banner) unless `isMessageComplianceActive` passes.
// Always fails open: a check error never blocks typing or sending.
export default function useComplianceCheck(text) {
  const appConfig = useAppConfig();
  const active = isMessageComplianceActive(appConfig, getUserType());

  const [violations, setViolations] = useState([]);
  const [severity, setSeverity] = useState("none");
  const [loading, setLoading] = useState(false);

  // Latest text, so async responses for stale drafts can be discarded.
  const latestText = useRef(text);
  latestText.current = text;

  // Debounced screen (regex + LLM, unpersisted) check while the agent types.
  useEffect(() => {
    if (!active) return undefined;
    if ((text || "").trim().length === 0) {
      setViolations([]);
      setSeverity("none");
      return undefined;
    }
    // Aborted on the next keystroke/unmount so superseded drafts don't keep
    // requests in flight.
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const res = await checkMessageCompliance(
          text,
          "screen",
          controller.signal,
        );
        if (latestText.current !== text) return; // stale draft, ignore
        const result = res?.result || INERT;
        setViolations(result.violations || []);
        setSeverity(result.severity || "none");
      } catch (e) {
        if (controller.signal.aborted) return;
        // Rate-limited: keep whatever warning is already showing.
        if (e?.response?.status === 429) return;
        if (latestText.current === text) {
          setViolations([]);
          setSeverity("none");
        }
      }
    }, COMPLIANCE_DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [text, active]);

  // Authoritative full (regex + LLM, paid) check — await once on Send.
  const runFullCheck = useCallback(async () => {
    if (!active || (text || "").trim().length === 0) return INERT;
    setLoading(true);
    try {
      const res = await checkMessageCompliance(text, "full");
      const result = res?.result || INERT;
      if (latestText.current === text) {
        setViolations(result.violations || []);
        setSeverity(result.severity || "none");
      }
      return result;
    } catch (e) {
      return INERT; // fail open — let the send proceed
    } finally {
      setLoading(false);
    }
  }, [text, active]);

  if (!active) {
    return { violations: [], severity: "none", loading: false, runFullCheck };
  }
  return { violations, severity, loading, runFullCheck };
}
